import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Paper,
  Divider,
  Avatar,
  Chip,
  LinearProgress,
  Fade,
  Grow,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
} from '@mui/material';
import { useGetSubscriptionQuery } from '../services/subscriptionApi';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  Area,
  AreaChart,
  RadialBarChart,
  RadialBar,
} from 'recharts';
import {
  People as PeopleIcon,
  AccessTime as AccessTimeIcon,
  HolidayVillage as LeaveIcon,
  Payment as PaymentIcon,
  TrendingUp as TrendingIcon,
  Analytics as AnalyticsIcon,
  Refresh as RefreshIcon,
  Business as BusinessIcon,
  Inventory as InventoryIcon,
  Timeline as ActivityIcon,
} from '@mui/icons-material';
import { useGetDashboardAnalyticsQuery } from '../store/api/dashboardApi';
import EmployeeAnalytics from './charts/EmployeeAnalytics';
import AttendanceAnalytics from './charts/AttendanceAnalytics';
import LeaveAnalytics from './charts/LeaveAnalytics';
import PayrollAnalytics from './charts/PayrollAnalytics';
import LoanAnalytics from './charts/LoanAnalytics';

const AnalyticsDashboard = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // Get subscription data for plan-based restrictions
  const {
    data: subscriptionData,
    isLoading: subscriptionLoading,
    error: subscriptionError
  } = useGetSubscriptionQuery();

  const {
    data: analyticsData,
    isLoading,
    error,
    refetch
  } = useGetDashboardAnalyticsQuery({
    month: selectedMonth,
    year: selectedYear
  });

  // Check if loan module is accessible based on subscription plan
  const isLoanAccessible = () => {
    // Super admin always has access
    if (user?.role === 'super_admin') return true;

    // If subscription is loading or there's an error, don't show the module
    if (subscriptionLoading || subscriptionError || !subscriptionData) return false;

    // Normalize subscription shape: support { success, data } or plain subscription
    const sub = subscriptionData.data ? subscriptionData.data : subscriptionData;

    // Check if trial is active (if trial expired, don't allow)
    if (sub.status === 'trial') {
      const now = new Date();
      const trialEndDate = sub.trialEndDate ? new Date(sub.trialEndDate) : null;
      if (trialEndDate && now > trialEndDate) return false; // Trial expired
    }

    // Check if plan includes loan module (DB contains 'loans' in modules)
    const planFeatures = sub.planId?.features;
    return planFeatures?.modules?.includes('loans') || false;
  };

  // Check if asset module is accessible based on subscription plan
  const isAssetAccessible = () => {
    // Super admin always has access
    if (user?.role === 'super_admin') return true;

    // If subscription is loading or there's an error, don't show the module
    if (subscriptionLoading || subscriptionError || !subscriptionData) return false;

    // Normalize subscription shape: support { success, data } or plain subscription
    const sub = subscriptionData.data ? subscriptionData.data : subscriptionData;

    // Check if trial is active (if trial expired, don't allow)
    if (sub.status === 'trial') {
      const now = new Date();
      const trialEndDate = sub.trialEndDate ? new Date(sub.trialEndDate) : null;
      if (trialEndDate && now > trialEndDate) return false; // Trial expired
    }

    // Check if plan includes assets module (DB contains 'assets' in modules)
    const planFeatures = sub.planId?.features;
    return planFeatures?.modules?.includes('assets') || false;
  };

  useEffect(() => {
    if (analyticsData) {
      console.log('Analytics data received:', analyticsData);
    }
  }, [analyticsData]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
        sx={{
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          borderRadius: 3,
          p: 4,
        }}
      >
        <Box sx={{ position: 'relative', mb: 3 }}>
          <CircularProgress
            size={80}
            thickness={4}
            sx={{
              color: '#667eea',
              '& .MuiCircularProgress-circle': {
                strokeLinecap: 'round',
              },
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 40,
              height: 40,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <AnalyticsIcon sx={{ color: 'white', fontSize: 20 }} />
          </Box>
        </Box>
        <Typography
          variant="h6"
          sx={{
            color: '#667eea',
            fontWeight: 600,
            textAlign: 'center',
            mb: 1
          }}
        >
          Loading Analytics Dashboard
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            textAlign: 'center',
            maxWidth: 300
          }}
        >
          Fetching real-time HR insights and performance metrics...
        </Typography>
        <LinearProgress
          sx={{
            width: 200,
            mt: 2,
            height: 4,
            borderRadius: 2,
            backgroundColor: 'rgba(102, 126, 234, 0.2)',
            '& .MuiLinearProgress-bar': {
              backgroundColor: '#667eea',
              borderRadius: 2,
            },
          }}
        />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Error loading analytics: {error.message || 'Something went wrong'}
      </Alert>
    );
  }

  const data = analyticsData?.data;

  // Define available tabs based on plan access
  const availableTabs = [
    {
      key: 'employees',
      label: 'Employees',
      icon: <PeopleIcon sx={{ fontSize: 20 }} />,
      component: (
        <EmployeeAnalytics
          data={data?.employeeStats}
          meta={data?.meta}
        />
      )
    },
    {
      key: 'attendance',
      label: 'Attendance',
      icon: <AccessTimeIcon sx={{ fontSize: 20 }} />,
      component: (
        <AttendanceAnalytics
          data={data?.attendanceStats}
          meta={data?.meta}
        />
      )
    },
    {
      key: 'leave',
      label: 'Leave Management',
      icon: <LeaveIcon sx={{ fontSize: 20 }} />,
      component: (
        <LeaveAnalytics
          data={data?.leaveStats}
          meta={data?.meta}
        />
      )
    },
    {
      key: 'payroll',
      label: 'Payroll',
      icon: <PaymentIcon sx={{ fontSize: 20 }} />,
      component: (
        <PayrollAnalytics
          data={data?.payrollStats}
          meta={data?.meta}
        />
      )
    },
    {
      key: 'loans',
      label: 'Loans',
      icon: <PaymentIcon sx={{ fontSize: 20 }} />,
      component: (
        <LoanAnalytics
          data={data?.loanStats}
          meta={data?.meta}
        />
      ),
      planCheck: isLoanAccessible()
    },
    {
      key: 'departments',
      label: 'Departments',
      icon: <BusinessIcon sx={{ fontSize: 20 }} />,
      component: (
        <Box>
          <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 3, fontWeight: 600, color: '#667eea' }}>
            Department Analytics
          </Typography>

          {/* Department Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{ bgcolor: '#e3f2fd', borderRadius: 2 }}>
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <Typography variant="h3" sx={{ color: '#1976d2', fontWeight: 'bold', mb: 1 }}>
                    {data.departmentStats?.summary?.totalDepartments || 0}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Total Departments
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{ bgcolor: '#e8f5e8', borderRadius: 2 }}>
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <Typography variant="h3" sx={{ color: '#2e7d32', fontWeight: 'bold', mb: 1 }}>
                    {data.departmentStats?.summary?.totalEmployees || 0}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Total Employees
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{ bgcolor: '#fff3e0', borderRadius: 2 }}>
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <Typography variant="h3" sx={{ color: '#f57c00', fontWeight: 'bold', mb: 1 }}>
                    {data.departmentStats?.summary?.activeEmployees || 0}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Active Employees
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Modern Charts */}
          <Grid container spacing={4} sx={{ mb: 4 }}>
            {/* Department Distribution Pie Chart */}
            <Grid item xs={12} md={6}>
              <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)' }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ textAlign: 'center', fontWeight: 600, color: '#667eea' }}>
                    Department Distribution
                  </Typography>
                  {data.departmentStats?.departmentBreakdown && data.departmentStats.departmentBreakdown.length > 0 ? (
                    <Box sx={{ height: 350 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={data.departmentStats.departmentBreakdown.map((dept, index) => ({
                              name: dept.name,
                              value: dept.employeeCount,
                              fill: ['#8884d8', '#82ca9d', '#ffc658', '#ff7c7c', '#8dd1e1', '#d084d0'][index % 6]
                            }))}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                            outerRadius={100}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {data.departmentStats.departmentBreakdown.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={['#8884d8', '#82ca9d', '#ffc658', '#ff7c7c', '#8dd1e1', '#d084d0'][index % 6]} />
                            ))}
                          </Pie>
                          <RechartsTooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </Box>
                  ) : (
                    <Box sx={{ height: 350, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        No department data available
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Employee Count by Department Bar Chart */}
            <Grid item xs={12} md={6}>
              <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)' }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ textAlign: 'center', fontWeight: 600, color: '#667eea' }}>
                    Employees by Department
                  </Typography>
                  {data.departmentStats?.departmentBreakdown && data.departmentStats.departmentBreakdown.length > 0 ? (
                    <Box sx={{ height: 350 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={data.departmentStats.departmentBreakdown}
                          margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis
                            dataKey="name"
                            angle={-45}
                            textAnchor="end"
                            height={80}
                            fontSize={12}
                          />
                          <YAxis />
                          <RechartsTooltip />
                          <Bar dataKey="employeeCount" fill="#8884d8" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  ) : (
                    <Box sx={{ height: 350, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        No department data available
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Active vs Inactive Employees Donut Chart */}
          <Grid container spacing={4} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)' }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ textAlign: 'center', fontWeight: 600, color: '#667eea' }}>
                    Employee Status Distribution
                  </Typography>
                  {data.departmentStats?.summary && (
                    <Box sx={{ height: 350 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={[
                              {
                                name: 'Active Employees',
                                value: data.departmentStats.summary.activeEmployees,
                                fill: '#82ca9d'
                              },
                              {
                                name: 'Inactive Employees',
                                value: data.departmentStats.summary.totalEmployees - data.departmentStats.summary.activeEmployees,
                                fill: '#ff7c7c'
                              }
                            ]}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={100}
                            paddingAngle={5}
                            dataKey="value"
                          >
                            <Cell fill="#82ca9d" />
                            <Cell fill="#ff7c7c" />
                          </Pie>
                          <RechartsTooltip />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Department Performance Metrics */}
            <Grid item xs={12} md={6}>
              <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)' }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ textAlign: 'center', fontWeight: 600, color: '#667eea', mb: 3 }}>
                    Department Performance
                  </Typography>
                  {data.departmentStats?.departmentBreakdown && data.departmentStats.departmentBreakdown.length > 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {data.departmentStats.departmentBreakdown.slice(0, 5).map((dept, index) => {
                        const activePercentage = dept.employeeCount > 0
                          ? Math.round((dept.activeEmployees / dept.employeeCount) * 100)
                          : 0;
                        return (
                          <Box key={index} sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 2, bgcolor: '#fafafa' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                {dept.name}
                              </Typography>
                              <Chip
                                label={`${activePercentage}% Active`}
                                size="small"
                                sx={{
                                  bgcolor: activePercentage >= 80 ? '#e8f5e8' :
                                           activePercentage >= 60 ? '#fff3e0' : '#ffebee',
                                  color: activePercentage >= 80 ? '#2e7d32' :
                                         activePercentage >= 60 ? '#f57c00' : '#d32f2f'
                                }}
                              />
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="body2" color="text.secondary">
                                {dept.activeEmployees} of {dept.employeeCount} employees
                              </Typography>
                              <Box sx={{ width: 100, mr: 1 }}>
                                <LinearProgress
                                  variant="determinate"
                                  value={activePercentage}
                                  sx={{
                                    height: 8,
                                    borderRadius: 4,
                                    bgcolor: '#e0e0e0',
                                    '& .MuiLinearProgress-bar': {
                                      bgcolor: activePercentage >= 80 ? '#4caf50' :
                                               activePercentage >= 60 ? '#ff9800' : '#f44336',
                                      borderRadius: 4
                                    }
                                  }}
                                />
                              </Box>
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  ) : (
                    <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        No department performance data available
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )
    },
    {
      key: 'assets',
      label: 'Assets',
      icon: <InventoryIcon sx={{ fontSize: 20 }} />,
      component: (
        <Box>
          <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 3, fontWeight: 600, color: '#667eea' }}>
            Asset Analytics
          </Typography>

          {/* Asset Summary */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ bgcolor: '#f3e5f5', borderRadius: 2 }}>
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <Typography variant="h3" sx={{ color: '#9c27b0', fontWeight: 'bold', mb: 1 }}>
                    {data.assetStats?.totalAssets || 0}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Total Assets
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ bgcolor: '#e8f5e8', borderRadius: 2 }}>
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <Typography variant="h3" sx={{ color: '#2e7d32', fontWeight: 'bold', mb: 1 }}>
                    {data.assetStats?.assignmentStats?.assigned || 0}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Assigned Assets
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ bgcolor: '#fff3e0', borderRadius: 2 }}>
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <Typography variant="h3" sx={{ color: '#f57c00', fontWeight: 'bold', mb: 1 }}>
                    {data.assetStats?.assignmentStats?.unassigned || 0}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Unassigned Assets
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ bgcolor: '#fce4ec', borderRadius: 2 }}>
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <Typography variant="h3" sx={{ color: '#c2185b', fontWeight: 'bold', mb: 1 }}>
                    {data.assetStats?.assignmentStats?.underMaintenance || 0}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Under Maintenance
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Modern Asset Charts */}
          <Grid container spacing={4} sx={{ mb: 4 }}>
            {/* Asset Status Distribution Pie Chart */}
            <Grid item xs={12} md={6}>
              <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)' }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ textAlign: 'center', fontWeight: 600, color: '#667eea' }}>
                    Asset Status Distribution
                  </Typography>
                  {data.assetStats?.byStatus && data.assetStats.byStatus.length > 0 ? (
                    <Box sx={{ height: 350 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={data.assetStats.byStatus.map((status, index) => ({
                              name: status.status,
                              value: status.count,
                              fill: status.status === 'assigned' ? '#82ca9d' :
                                    status.status === 'available' ? '#ffc658' :
                                    status.status === 'under_maintenance' ? '#ff7c7c' :
                                    status.status === 'retired' ? '#8dd1e1' : '#d084d0'
                            }))}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                            outerRadius={100}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {data.assetStats.byStatus.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={
                                entry.status === 'assigned' ? '#82ca9d' :
                                entry.status === 'available' ? '#ffc658' :
                                entry.status === 'under_maintenance' ? '#ff7c7c' :
                                entry.status === 'retired' ? '#8dd1e1' : '#d084d0'
                              } />
                            ))}
                          </Pie>
                          <RechartsTooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </Box>
                  ) : (
                    <Box sx={{ height: 350, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        No asset status data available
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Assets by Category Bar Chart */}
            <Grid item xs={12} md={6}>
              <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)' }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ textAlign: 'center', fontWeight: 600, color: '#667eea' }}>
                    Assets by Category
                  </Typography>
                  {data.assetStats?.byCategory && data.assetStats.byCategory.length > 0 ? (
                    <Box sx={{ height: 350 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={data.assetStats.byCategory}
                          margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis
                            dataKey="category"
                            angle={-45}
                            textAnchor="end"
                            height={80}
                            fontSize={12}
                          />
                          <YAxis />
                          <RechartsTooltip />
                          <Bar dataKey="count" fill="#8884d8" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  ) : (
                    <Box sx={{ height: 350, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        No asset category data available
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Asset Assignment Status Donut Chart */}
          <Grid container spacing={4} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)' }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ textAlign: 'center', fontWeight: 600, color: '#667eea' }}>
                    Asset Assignment Status
                  </Typography>
                  {data.assetStats?.assignmentStats && (
                    <Box sx={{ height: 350 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={[
                              {
                                name: 'Assigned',
                                value: data.assetStats.assignmentStats.assigned,
                                fill: '#82ca9d'
                              },
                              {
                                name: 'Unassigned',
                                value: data.assetStats.assignmentStats.unassigned,
                                fill: '#ffc658'
                              },
                              {
                                name: 'Under Maintenance',
                                value: data.assetStats.assignmentStats.underMaintenance,
                                fill: '#ff7c7c'
                              },
                              {
                                name: 'Retired',
                                value: data.assetStats.assignmentStats.retired || 0,
                                fill: '#8dd1e1'
                              }
                            ]}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={100}
                            paddingAngle={5}
                            dataKey="value"
                          >
                            <Cell fill="#82ca9d" />
                            <Cell fill="#ffc658" />
                            <Cell fill="#ff7c7c" />
                            <Cell fill="#8dd1e1" />
                          </Pie>
                          <RechartsTooltip />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Asset Utilization Metrics */}
            <Grid item xs={12} md={6}>
              <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)' }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ textAlign: 'center', fontWeight: 600, color: '#667eea', mb: 3 }}>
                    Asset Utilization Overview
                  </Typography>
                  {data.assetStats?.assignmentStats && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 2, bgcolor: '#e8f5e8' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            Assigned Assets
                          </Typography>
                          <Chip
                            label={`${Math.round((data.assetStats.assignmentStats.assigned / data.assetStats.totalAssets) * 100) || 0}%`}
                            sx={{ bgcolor: '#2e7d32', color: 'white' }}
                          />
                        </Box>
                        <Box sx={{ width: '100%', mr: 1 }}>
                          <LinearProgress
                            variant="determinate"
                            value={(data.assetStats.assignmentStats.assigned / data.assetStats.totalAssets) * 100}
                            sx={{
                              height: 10,
                              borderRadius: 5,
                              bgcolor: '#e0e0e0',
                              '& .MuiLinearProgress-bar': {
                                bgcolor: '#4caf50',
                                borderRadius: 5
                              }
                            }}
                          />
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          {data.assetStats.assignmentStats.assigned} of {data.assetStats.totalAssets} assets in use
                        </Typography>
                      </Box>

                      <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 2, bgcolor: '#fff3e0' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            Available Assets
                          </Typography>
                          <Chip
                            label={`${Math.round((data.assetStats.assignmentStats.unassigned / data.assetStats.totalAssets) * 100) || 0}%`}
                            sx={{ bgcolor: '#f57c00', color: 'white' }}
                          />
                        </Box>
                        <Box sx={{ width: '100%', mr: 1 }}>
                          <LinearProgress
                            variant="determinate"
                            value={(data.assetStats.assignmentStats.unassigned / data.assetStats.totalAssets) * 100}
                            sx={{
                              height: 10,
                              borderRadius: 5,
                              bgcolor: '#e0e0e0',
                              '& .MuiLinearProgress-bar': {
                                bgcolor: '#ff9800',
                                borderRadius: 5
                              }
                            }}
                          />
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          {data.assetStats.assignmentStats.unassigned} assets ready for assignment
                        </Typography>
                      </Box>

                      <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 2, bgcolor: '#fce4ec' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            Under Maintenance
                          </Typography>
                          <Chip
                            label={`${Math.round((data.assetStats.assignmentStats.underMaintenance / data.assetStats.totalAssets) * 100) || 0}%`}
                            sx={{ bgcolor: '#c2185b', color: 'white' }}
                          />
                        </Box>
                        <Box sx={{ width: '100%', mr: 1 }}>
                          <LinearProgress
                            variant="determinate"
                            value={(data.assetStats.assignmentStats.underMaintenance / data.assetStats.totalAssets) * 100}
                            sx={{
                              height: 10,
                              borderRadius: 5,
                              bgcolor: '#e0e0e0',
                              '& .MuiLinearProgress-bar': {
                                bgcolor: '#e91e63',
                                borderRadius: 5
                              }
                            }}
                          />
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          {data.assetStats.assignmentStats.underMaintenance} assets currently being serviced
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      ),
      planCheck: isAssetAccessible()
    },
    {
      key: 'activities',
      label: 'Activities',
      icon: <ActivityIcon sx={{ fontSize: 20 }} />,
      component: (
        <Box>
          <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 3, fontWeight: 600, color: '#667eea' }}>
            Recent Activities
          </Typography>

          {data.recentActivities && data.recentActivities.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {data.recentActivities.map((activity, index) => (
                <Card key={activity.id || index} sx={{
                  borderRadius: 2,
                  border: '1px solid #e0e0e0',
                  transition: 'box-shadow 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                  }
                }}>
                  <CardContent sx={{ py: 2, px: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar sx={{
                        bgcolor: activity.type === 'employee' ? '#e3f2fd' :
                                activity.type === 'attendance' ? '#e8f5e8' :
                                activity.type === 'leave' ? '#fff3e0' :
                                activity.type === 'payroll' ? '#f3e5f5' : '#fafafa',
                        width: 40,
                        height: 40
                      }}>
                        <Typography variant="body2" sx={{
                          color: activity.type === 'employee' ? '#1976d2' :
                                 activity.type === 'attendance' ? '#2e7d32' :
                                 activity.type === 'leave' ? '#f57c00' :
                                 activity.type === 'payroll' ? '#9c27b0' : '#666'
                        }}>
                          {activity.icon || '📋'}
                        </Typography>
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
                          {activity.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {activity.description}
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {activity.date ? new Date(activity.date).toLocaleDateString() : 'N/A'}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                No recent activities available
              </Typography>
            </Box>
          )}
        </Box>
      )
    }
  ];

  // Filter tabs based on plan access
  const filteredTabs = availableTabs.filter(tab => !tab.planCheck || tab.planCheck);

  return (
    <Fade in={true} timeout={800}>
      <Box sx={{ width: '100%', p: 2 }}>
        {/* Header */}
        <Box
          sx={{
            mb: 2,
            p: 2,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            boxShadow: '0 4px 20px rgba(102, 126, 234, 0.2)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Avatar
                sx={{
                  mr: 1.5,
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  width: 36,
                  height: 36
                }}
              >
                <AnalyticsIcon sx={{ fontSize: 20 }} />
              </Avatar>
              <Box>
                <Typography
                  variant="h4"
                  component="h1"
                  fontWeight={600}
                  sx={{ fontSize: { xs: '1.4rem', md: '1.6rem' }, lineHeight: 1.2 }}
                >
                  HR Analytics Dashboard
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    opacity: 0.9,
                    fontSize: '0.85rem',
                    lineHeight: 1.2
                  }}
                >
                  Real-time HR insights & performance metrics
                </Typography>
              </Box>
            </Box>
            <Tooltip title="Refresh Analytics Data">
              <IconButton
                onClick={() => refetch()}
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.3)',
                  },
                  width: 32,
                  height: 32
                }}
              >
                <RefreshIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Filter Controls in Header */}
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography variant="body2" sx={{ fontWeight: 500, opacity: 0.9, fontSize: '0.8rem' }}>
              Filter:
            </Typography>
            <FormControl size="small" sx={{ minWidth: 90 }}>
              <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.8)', '&.Mui-focused': { color: 'white' } }}>Month</InputLabel>
              <Select
                value={selectedMonth}
                label="Month"
                onChange={(e) => setSelectedMonth(e.target.value)}
                sx={{
                  borderRadius: 1,
                  fontSize: '0.8rem',
                  color: 'white',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'white',
                  },
                  '& .MuiSvgIcon-root': {
                    color: 'white',
                  }
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      bgcolor: '#667eea',
                      '& .MuiMenuItem-root': {
                        color: 'white',
                        '&:hover': {
                          bgcolor: 'rgba(255, 255, 255, 0.1)',
                        },
                        '&.Mui-selected': {
                          bgcolor: 'rgba(255, 255, 255, 0.2)',
                        }
                      }
                    }
                  }
                }}
              >
                {Array.from({ length: 12 }, (_, i) => (
                  <MenuItem key={i + 1} value={i + 1}>
                    {new Date(0, i).toLocaleString('default', { month: 'short' })}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 90 }}>
              <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.8)', '&.Mui-focused': { color: 'white' } }}>Year</InputLabel>
              <Select
                value={selectedYear}
                label="Year"
                onChange={(e) => setSelectedYear(e.target.value)}
                sx={{
                  borderRadius: 1,
                  fontSize: '0.8rem',
                  color: 'white',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'white',
                  },
                  '& .MuiSvgIcon-root': {
                    color: 'white',
                  }
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      bgcolor: '#667eea',
                      '& .MuiMenuItem-root': {
                        color: 'white',
                        '&:hover': {
                          bgcolor: 'rgba(255, 255, 255, 0.1)',
                        },
                        '&.Mui-selected': {
                          bgcolor: 'rgba(255, 255, 255, 0.2)',
                        }
                      }
                    }
                  }
                }}
              >
                {Array.from({ length: 5 }, (_, i) => {
                  const year = new Date().getFullYear() - 2 + i;
                  return (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>

            <Button
              variant="contained"
              size="small"
              onClick={() => refetch()}
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                color: 'white',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.3)',
                },
                borderRadius: 1,
                px: 1.5,
                fontSize: '0.75rem',
                minWidth: 'auto'
              }}
            >
              Apply
            </Button>

            <Typography variant="caption" sx={{ opacity: 0.8, fontSize: '0.75rem', ml: 1 }}>
              {new Date(selectedYear, selectedMonth - 1).toLocaleString('default', { month: 'short', year: 'numeric' })}
            </Typography>
          </Box>
        </Box>



        {/* Key Metrics Overview */}
        {data && (
          <Box sx={{ mb: 2 }}>
            <Box
              sx={{
                display: 'flex',
                gap: 1.5,
                overflowX: 'auto',
                pb: 1.5,
                '&::-webkit-scrollbar': {
                  height: '4px',
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: 'rgba(0,0,0,0.1)',
                  borderRadius: '2px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: 'rgba(102, 126, 234, 0.3)',
                  borderRadius: '2px',
                  '&:hover': {
                    backgroundColor: 'rgba(102, 126, 234, 0.5)',
                  },
                },
              }}
            >
              {/* Total Employees */}
              <Card
                sx={{
                  minWidth: 140,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  borderRadius: 2,
                  boxShadow: '0 2px 8px rgba(102, 126, 234, 0.2)',
                  transition: 'transform 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                  }
                }}
              >
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Avatar sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', width: 24, height: 24 }}>
                      <PeopleIcon sx={{ color: 'white', fontSize: 14 }} />
                    </Avatar>
                    <Typography variant="h6" fontWeight={600} sx={{ fontSize: '1.1rem' }}>
                      {data.employeeStats?.total || 0}
                    </Typography>
                  </Box>
                  <Typography variant="caption" sx={{ opacity: 0.9, fontSize: '0.7rem', lineHeight: 1.2 }}>
                    Total Employees
                  </Typography>
                </CardContent>
              </Card>

              {/* Avg Attendance Rate */}
              <Card
                sx={{
                  minWidth: 140,
                  background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                  color: 'white',
                  borderRadius: 2,
                  boxShadow: '0 2px 8px rgba(245, 87, 108, 0.2)',
                  transition: 'transform 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(245, 87, 108, 0.3)',
                  }
                }}
              >
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Avatar sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', width: 24, height: 24 }}>
                      <AccessTimeIcon sx={{ color: 'white', fontSize: 14 }} />
                    </Avatar>
                    <Typography variant="h6" fontWeight={600} sx={{ fontSize: '1.1rem' }}>
                      {data.attendanceStats?.keyMetrics?.avgAttendanceRate || 0}%
                    </Typography>
                  </Box>
                  <Typography variant="caption" sx={{ opacity: 0.9, fontSize: '0.7rem', lineHeight: 1.2 }}>
                    Avg Attendance
                  </Typography>
                </CardContent>
              </Card>

              {/* Total Leave Days */}
              <Card
                sx={{
                  minWidth: 140,
                  background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                  color: 'white',
                  borderRadius: 2,
                  boxShadow: '0 2px 8px rgba(79, 172, 254, 0.2)',
                  transition: 'transform 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(79, 172, 254, 0.3)',
                  }
                }}
              >
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Avatar sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', width: 24, height: 24 }}>
                      <LeaveIcon sx={{ color: 'white', fontSize: 14 }} />
                    </Avatar>
                    <Typography variant="h6" fontWeight={600} sx={{ fontSize: '1.1rem' }}>
                      {data.leaveStats?.keyMetrics?.totalLeaveDays || 0}
                    </Typography>
                  </Box>
                  <Typography variant="caption" sx={{ opacity: 0.9, fontSize: '0.7rem', lineHeight: 1.2 }}>
                    Total Leave Days
                  </Typography>
                </CardContent>
              </Card>

              {/* Payroll Cost */}
              <Card
                sx={{
                  minWidth: 140,
                  background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                  color: 'white',
                  borderRadius: 2,
                  boxShadow: '0 2px 8px rgba(67, 233, 123, 0.2)',
                  transition: 'transform 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(67, 233, 123, 0.3)',
                  }
                }}
              >
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Avatar sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', width: 24, height: 24 }}>
                      <PaymentIcon sx={{ color: 'white', fontSize: 14 }} />
                    </Avatar>
                    <Typography variant="body1" fontWeight={600} sx={{ fontSize: '0.9rem' }}>
                      ₹{data.payrollStats?.keyMetrics?.totalPayrollCost?.toLocaleString() || 0}
                    </Typography>
                  </Box>
                  <Typography variant="caption" sx={{ opacity: 0.9, fontSize: '0.7rem', lineHeight: 1.2 }}>
                    Payroll Cost
                  </Typography>
                </CardContent>
              </Card>

              {/* Total Loan Amount - Only show if loan module is accessible */}
              {isLoanAccessible() && (
                <Card
                  sx={{
                    minWidth: 140,
                    background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                    color: 'white',
                    borderRadius: 2,
                    boxShadow: '0 2px 8px rgba(250, 112, 154, 0.2)',
                    transition: 'transform 0.2s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 12px rgba(250, 112, 154, 0.3)',
                    }
                  }}
                >
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Avatar sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', width: 24, height: 24 }}>
                        <PaymentIcon sx={{ color: 'white', fontSize: 14 }} />
                      </Avatar>
                      <Typography variant="body1" fontWeight={600} sx={{ fontSize: '0.9rem' }}>
                        ₹{data.loanStats?.keyMetrics?.totalLoanAmount?.toLocaleString() || 0}
                      </Typography>
                    </Box>
                    <Typography variant="caption" sx={{ opacity: 0.9, fontSize: '0.7rem', lineHeight: 1.2 }}>
                      Total Loans
                    </Typography>
                  </CardContent>
                </Card>
              )}
            </Box>
          </Box>
        )}

        {/* Tabbed Analytics */}
        <Paper
          elevation={0}
          sx={{
            width: '100%',
            borderRadius: 3,
            overflow: 'hidden',
            background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
            border: '1px solid rgba(0, 0, 0, 0.08)',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          }}
        >
          <Box
            sx={{
              background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
              borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
              px: 2,
            }}
          >
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              sx={{
                '& .MuiTab-root': {
                  minHeight: 48,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  borderRadius: 2,
                  mx: 0.5,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(102, 126, 234, 0.08)',
                    color: '#667eea',
                  },
                  '&.Mui-selected': {
                    backgroundColor: '#667eea',
                    color: 'white',
                    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                  },
                },
                '& .MuiTabs-indicator': {
                  display: 'none',
                },
              }}
            >
              {filteredTabs.map((tab, index) => (
                <Tab
                  key={tab.key}
                  icon={tab.icon}
                  label={tab.label}
                  iconPosition="start"
                  sx={{ minWidth: 100 }}
                />
              ))}
            </Tabs>
          </Box>

          <Box
            sx={{
              p: 2,
              background: 'linear-gradient(180deg, #ffffff 0%, #fafbfc 100%)',
              minHeight: 300,
            }}
          >
            <Fade in={true} timeout={500} key={activeTab}>
              <Box>
                {filteredTabs[activeTab]?.component}
              </Box>
            </Fade>
          </Box>
        </Paper>


      </Box>
    </Fade>
  );
};

export default AnalyticsDashboard;
